import { CalendarOutlined, RightOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, colors, semanticColors } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { AntColumnType } from '@revenuemarket/ui/antd/Table';
import { displayCounterPartNumber } from '@revenuemarket/utils';
import { Badge, Button, DatePicker, Typography } from 'antd';
import dayjs from 'dayjs';

import { InvoiceMapping } from '~apis/mapping';
import SettlementTaxInvoiceResultDrawer from '~components/BusinessEntity/SettlementTaxInvoiceResultDrawer';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { TaxInvoiceType } from '~models/tax-invoice';
import { mixpanel } from '~pages/_app';

import MapTransactionToSettlementBtn from './MapTransactionToSettlementBtn';
import { formatNumByTaxInvoice, getStautsForBadge } from './utils';

export const headerStyle = {
  background: 'white',
  color: colorTokens.textDescription,
  borderTop: `1px solid ${colorTokens.borderDefault}`,
};

export const COLUMNS = ({
  onUpdateDepositDate,
  onMappingSuccess,
  showYear,
  type,
  wiringEnabled,
}: {
  onMappingSuccess: () => void;
  onUpdateDepositDate: ({
    depositDate,
    item,
  }: {
    depositDate: string | null;
    item: InvoiceMapping;
  }) => void;
  showYear: boolean;
  type: TaxInvoiceType;
  wiringEnabled?: boolean;
}): AntColumnType<InvoiceMapping> => {
  return [
    {
      title: '발급일자',
      dataIndex: 'issueDate',
      key: 'issueDate',
      sorter: true,
      width: 70,
      className: css`
        color: ${colorTokens.textDescription};
      `,
      render: (value) => {
        return dayjs(value).format(showYear ? 'YY-MM-DD' : 'MM-DD');
      },
    },
    {
      title: '작성일자',
      dataIndex: 'reportingDate',
      key: 'reportingDate',
      sorter: true,
      width: 70,
      className: css`
        color: ${colorTokens.textDescription};
      `,
      render: (value) => {
        return dayjs(value).format(showYear ? 'YY-MM-DD' : 'MM-DD');
      },
    },
    {
      title: '거래처',
      dataIndex: 'businessEntity',
      key: 'businessEntity',
      width: 200,
      className: css`
        min-width: 190px;
      `,
      render(_, record) {
        return (
          <Flex
            direction="column"
            style={{
              cursor: record.businessEntityId ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (!record.businessEntityId) return;
              location.href = `/clobe/sales-purchase/${
                record.businessEntityId
              }?type=${type.toLowerCase()}Ledger`;
            }}
          >
            <Typography.Text style={{ fontSize: 14 }}>
              {record.counterPartyCompanyName}
            </Typography.Text>
            <Typography.Text style={{ color: colorTokens.textDescription }}>
              {displayCounterPartNumber(record.counterPartyBusinessRegNumber)}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: '대표 품목',
      dataIndex: 'repItems',
      key: 'repItems',
      width: 200,
      render: (value, record) => {
        return (
          <Flex
            style={{
              cursor: 'pointer',
            }}
            gap={4}
            align="center"
            onClick={() => {
              overlays.open(({ isOpen, onClose }) => (
                <SettlementTaxInvoiceResultDrawer
                  isOpen={isOpen}
                  onClose={onClose}
                  id={record.taxInvoiceId}
                  type={record.taxInvoiceType}
                />
              ));
            }}
          >
            <Typography.Text
              style={{
                fontSize: 14,
                color: !value ? colorTokens.textDescription : colorTokens.textDefault,
              }}
            >
              {value || '품목 정보 없음'}
            </Typography.Text>
            <RightOutlined
              style={{
                color: colorTokens.iconDefault,
              }}
            />
          </Flex>
        );
      },
    },
    {
      title: '세금계산서 합계',
      align: 'right',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (value) => {
        return <Typography.Text style={{ fontSize: 14 }}>{value.toLocaleString()}</Typography.Text>;
      },
    },
    {
      title: '정산 합계',
      dataIndex: 'mappingAmount',
      key: 'mappingAmount',
      align: 'right',
      width: 120,
      render: (value, record) => {
        return value ? (
          <Typography.Text style={{ fontSize: 14 }}>
            {record.status === 'DONE'
              ? record.totalAmount.toLocaleString()
              : formatNumByTaxInvoice(value, record.taxInvoiceType).toLocaleString()}
          </Typography.Text>
        ) : (
          '-'
        );
      },
    },
    {
      title: '미정산 잔액',
      dataIndex: 'unmappedAmount',
      key: 'unmappedAmount',
      align: 'right',
      width: 120,
      render: (value, record) => {
        return record.status === 'DONE' ? (
          '-'
        ) : record.totalAmount -
          formatNumByTaxInvoice(record.mappingAmount, record.taxInvoiceType) ? (
          <Typography.Text
            style={{
              fontSize: 14,
              color: record.status === 'DELAYED' ? '#F74B46' : colors.grayscaleBlack,
            }}
          >
            {(
              record.totalAmount -
              formatNumByTaxInvoice(record.mappingAmount, record.taxInvoiceType)
            ).toLocaleString()}
          </Typography.Text>
        ) : (
          '-'
        );
      },
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 80,
      render: (value) => {
        return (
          <Badge
            status={getStautsForBadge(value).status}
            text={
              <Typography.Text style={{ fontSize: 14 }}>
                {getStautsForBadge(value).text}
              </Typography.Text>
            }
          />
        );
      },
    },
    {
      title: '정산 예정일자',
      dataIndex: 'expectedPaymentDate',
      key: 'expectedPaymentDate',
      sorter: true,
      width: 140,
      render: (depositDate: string, record) => {
        return (
          <Flex align="center">
            <CalendarOutlined
              width={16}
              height={16}
              style={{
                color: colorTokens.iconDefault,
              }}
            />

            <PermissionGuard
              required={{
                type: 'taxInvoicePermissions',
              }}
              fallback={
                <Typography.Text style={{ fontSize: 14 }}>
                  {depositDate ? dayjs(depositDate).format('YYYY-MM-DD') : ''}
                </Typography.Text>
              }
            >
              <DatePicker
                clearIcon={null}
                value={depositDate ? dayjs(depositDate) : null}
                bordered={false}
                suffixIcon={null}
                placeholder="날짜 선택"
                panelRender={(panel) => {
                  return (
                    <div
                      className={css`
                        &&& .ant-picker-today-btn {
                          display: none;
                        }

                        &&& .ant-picker-footer-extra {
                          display: flex;
                          justify-content: center;
                        }
                      `}
                    >
                      {panel}
                    </div>
                  );
                }}
                className={css`
                  &&& .ant-picker-input > input {
                    font-size: 14px;
                  }
                `}
                allowClear
                renderExtraFooter={(e) => {
                  return (
                    <Button
                      type="link"
                      style={{
                        textAlign: 'center',
                      }}
                      className="ant-picker-clear"
                      onClick={() => {
                        // @ts-ignore
                        document.activeElement?.blur(); // 패널을 닫음
                        if (onUpdateDepositDate) {
                          onUpdateDepositDate({
                            depositDate: '',
                            item: record,
                          });
                        }
                      }}
                    >
                      정산날짜 초기화
                    </Button>
                  );
                }}
                onChange={(depositDate) => {
                  if (onUpdateDepositDate) {
                    mixpanel.track(MIXPANEL_EVENT.SETTLEMENT.DATE_CHANGE);
                    onUpdateDepositDate({
                      depositDate: depositDate?.format('YYYY-MM-DD') ?? '',
                      item: record,
                    });
                    mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
                      type: 'update settlement date',
                    });
                  }
                }}
              />
            </PermissionGuard>
          </Flex>
        );
      },
    },
    {
      title: '실제 정산일자',
      dataIndex: 'actualPaymentDate',
      key: 'actualPaymentDate',
      width: 140,
      render: (value, record) => {
        const diff = dayjs(record.actualPaymentDate).diff(record.expectedPaymentDate, 'd');
        return value ? (
          <Flex align="center" gap={4}>
            <Typography.Text style={{ fontSize: 14 }}>{value}</Typography.Text>
            {diff !== 0 && record.expectedPaymentDate && (
              <Typography.Text
                style={{
                  color: diff > 0 ? semanticColors.danger.medium : colorTokens.textDescription,
                }}
              >{`(${diff > 0 ? '+' : ''}${diff})`}</Typography.Text>
            )}
          </Flex>
        ) : (
          '-'
        );
      },
    },
    {
      title: '정산 내역',
      dataIndex: 'mappedCount',
      key: 'mappedCount',
      width: 225,
      render: (_, record) => {
        return (
          <Flex align="center" gap={8}>
            <MapTransactionToSettlementBtn {...record} onMappingSuccess={onMappingSuccess} />
          </Flex>
        );
      },
    },
  ];
};
